import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { Plus, Trash2, Edit2 } from 'lucide-react';
import { ApprovalBadge, RowActions, PendingApprovalBanner, PendingCountBadge } from '../components/ApprovalActions';
import { formatCurrency, formatDate, today } from '../utils/helpers';

const emptyEx = { expense_date:today(), category_id:'', vehicle_id:'', description:'', amount:0, payment_mode:'cash', bank_id:'', paid_to:'', reference_no:'', notes:'' };
const emptyCat = { name:'', type:'operational', status:1 };

export default function Expenses() {
  const [tab, setTab] = useState('expenses');
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [banks, setBanks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyEx);
  const [editId, setEditId] = useState(null);
  const [catOpen, setCatOpen] = useState(false);
  const [catForm, setCatForm] = useState(emptyCat);
  const [catEditId, setCatEditId] = useState(null);

  const load = () => api.get('/expenses').then(r => setList(r.data.data || []));
  const loadCats = () => api.get('/expense-categories').then(r => setCats(r.data.data || []));
  useEffect(() => { load(); loadCats(); api.get('/vehicles').then(r => setVehicles(r.data.data || [])); api.get('/banks').then(r => setBanks((r.data.data||[]).filter(b => b.status == 1))); }, []);

  const save = async () => {
    if (!form.amount) return toast.error('Amount required');
    try {
      if (editId) await api.put('/expenses/' + editId, form);
      else await api.post('/expenses', form);
      toast.success('Saved'); setOpen(false); setForm(emptyEx); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };
  const edit = (e) => { setForm({...emptyEx, ...e}); setEditId(e.id); setOpen(true); };

  const saveCat = async () => {
    if (!catForm.name) return toast.error('Name required');
    try {
      if (catEditId) await api.put('/expense-categories/' + catEditId, catForm);
      else await api.post('/expense-categories', catForm);
      toast.success('Saved'); setCatOpen(false); setCatForm(emptyCat); setCatEditId(null); loadCats();
    } catch (e) { toast.error('Error'); }
  };
  const delCat = async (id) => { if (!window.confirm('Delete?')) return; try { await api.delete('/expense-categories/' + id); loadCats(); toast.success('Deleted'); } catch (e) { toast.error(e?.response?.data?.message || 'Error'); } };
  const editCat = (c) => { setCatForm({...emptyCat, ...c}); setCatEditId(c.id); setCatOpen(true); };

  const total = list.reduce((s, r) => s + parseFloat(r.amount||0), 0);
  const vehicleExp = list.filter(e => e.category_type === 'vehicle').reduce((s, r) => s + parseFloat(r.amount||0), 0);
  const adminExp = list.filter(e => e.category_type === 'administrative').reduce((s, r) => s + parseFloat(r.amount||0), 0);
  const salaryExp = list.filter(e => e.category_type === 'salary').reduce((s, r) => s + parseFloat(r.amount||0), 0);

  const pendingCount = list.filter(x => x.approval_status === 'pending').length;
  return (
    <div>
      <div className="sub-tabs">
        <div className={'sub-tab ' + (tab==='expenses'?'active':'')} onClick={()=>setTab('expenses')}>Expenses</div>
        <div className={'sub-tab ' + (tab==='categories'?'active':'')} onClick={()=>setTab('categories')}>Expense Categories</div>
      </div>

      {tab === 'expenses' && (
        <div className="card">
          <div className="stats-grid">
            <div className="stat-card"><div className="stat-label">Total Expenses</div><div className="stat-value">{formatCurrency(total)}</div></div>
            <div className="stat-card orange"><div className="stat-label">Vehicle OPEX</div><div className="stat-value">{formatCurrency(vehicleExp)}</div></div>
            <div className="stat-card"><div className="stat-label">Administrative</div><div className="stat-value">{formatCurrency(adminExp)}</div></div>
            <div className="stat-card purple"><div className="stat-label">Salary</div><div className="stat-value">{formatCurrency(salaryExp)}</div></div>
          </div>
          <div className="card-header">
            <h3>Operational Expenditures ({list.length})<PendingCountBadge count={pendingCount}/></h3>
            <button className="btn btn-blue" onClick={() => { setForm(emptyEx); setEditId(null); setOpen(true); }}><Plus size={14}/> Add Expense</button>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Expense No</th><th>Date</th><th>Category</th><th>Description</th><th>Paid To</th><th>Vehicle</th><th>Mode</th><th className="td-num">Amount</th><th>Approval</th><th>Actions</th></tr></thead>
              <tbody>
                {list.length ? list.map(e => (
                  <tr key={e.id} style={e.approval_status==='pending'?{background:'#fffbeb'}:{}}>
                    <td><strong>{e.expense_no}</strong></td>
                    <td>{formatDate(e.expense_date)}</td>
                    <td><span className="badge badge-blue">{e.category_name || '-'}</span></td>
                    <td>{e.description}</td>
                    <td>{e.paid_to || '-'}</td>
                    <td>{e.vehicle_no || '-'}</td>
                    <td><span className="badge badge-gray">{e.payment_mode}</span></td>
                    <td className="td-num">{formatCurrency(e.amount)}</td>
                    <td>
                      <RowActions endpoint="/expenses" row={e} onChange={load} onEdit={edit}/>
                    </td>
                  </tr>
                )) : <tr><td colSpan="10" className="empty">No expenses</td></tr>}
              </tbody>
            </table>
          </div>

          <Modal open={open} onClose={()=>setOpen(false)} title={editId?'Edit Expense':'Add Expense'} size="lg"
            footer={<>
              <button className="btn btn-gray" onClick={()=>setOpen(false)}>Cancel</button>
              <button className="btn btn-blue" onClick={save}>{editId?'Update':'Save'}</button>
            </>}>
            <div className="form-grid">
              <div className="form-group"><label>Date *</label><input type="date" value={form.expense_date} onChange={e=>setForm({...form, expense_date:e.target.value})}/></div>
              <div className="form-group"><label>Category *</label>
                <select value={form.category_id} onChange={e=>setForm({...form, category_id:e.target.value})}>
                  <option value="">-- Select --</option>
                  {cats.map(c=><option key={c.id} value={c.id}>{c.name} ({c.type})</option>)}
                </select>
              </div>
              <div className="form-group"><label>Vehicle (if any)</label>
                <select value={form.vehicle_id} onChange={e=>setForm({...form, vehicle_id:e.target.value})}>
                  <option value="">-- None --</option>
                  {vehicles.map(v=><option key={v.id} value={v.id}>{v.vehicle_no}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Amount *</label><input type="number" value={form.amount} onChange={e=>setForm({...form, amount:e.target.value})}/></div>
              <div className="form-group"><label>Paid To</label><input value={form.paid_to} onChange={e=>setForm({...form, paid_to:e.target.value})}/></div>
              <div className="form-group"><label>Payment Mode</label>
                <select value={form.payment_mode} onChange={e=>setForm({...form, payment_mode:e.target.value, bank_id: e.target.value==='cash'?'':form.bank_id})}>
                  <option value="cash">Cash</option><option value="bank">Bank</option><option value="upi">UPI</option><option value="cheque">Cheque</option>
                </select>
              </div>
              <div className="form-group"><label>Bank / Cash Account</label>
                <select value={form.bank_id} onChange={e=>setForm({...form, bank_id:e.target.value})}>
                  <option value="">-- None (manual) --</option>
                  {banks.filter(b => form.payment_mode==='cash' ? b.account_type==='cash' : b.account_type!=='cash').map(b=><option key={b.id} value={b.id}>{b.bank_name}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Reference No</label><input value={form.reference_no} onChange={e=>setForm({...form, reference_no:e.target.value})}/></div>
              <div className="form-group full-width"><label>Description</label><input value={form.description} onChange={e=>setForm({...form, description:e.target.value})}/></div>
              <div className="form-group full-width"><label>Notes</label><textarea value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})}/></div>
            </div>
          </Modal>
        </div>
      )}

      {tab === 'categories' && (
        <div className="card">
          <div className="card-header">
            <h3>Expense Categories ({cats.length})</h3>
            <button className="btn btn-blue" onClick={() => { setCatForm(emptyCat); setCatEditId(null); setCatOpen(true); }}><Plus size={14}/> Add Category</button>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Name</th><th>Type</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {cats.length ? cats.map(c => (
                  <tr key={c.id} style={c.approval_status==='pending'?{background:'#fffbeb'}:{}}>
                    <td><strong>{c.name}</strong></td>
                    <td><span className="badge badge-blue">{c.type}</span></td>
                    <td><span className={'badge ' + (c.status==1?'badge-green':'badge-red')}>{c.status==1?'Active':'Inactive'}</span></td>
                    <td>
                      <button className="btn btn-sm btn-gray" onClick={()=>editCat(c)}><Edit2 size={12}/></button>{' '}
                      <button className="btn btn-sm btn-red" onClick={()=>delCat(c.id)}><Trash2 size={12}/></button>
                    </td>
                  </tr>
                )) : <tr><td colSpan="4" className="empty">No categories</td></tr>}
              </tbody>
            </table>
          </div>
          <Modal open={catOpen} onClose={()=>setCatOpen(false)} title={catEditId?'Edit Category':'Add Category'}
            footer={<>
              <button className="btn btn-gray" onClick={()=>setCatOpen(false)}>Cancel</button>
              <button className="btn btn-blue" onClick={saveCat}>{catEditId?'Update':'Save'}</button>
            </>}>
            <div className="form-grid">
              <div className="form-group full-width"><label>Name *</label><input value={catForm.name} onChange={e=>setCatForm({...catForm, name:e.target.value})}/></div>
              <div className="form-group"><label>Type</label>
                <select value={catForm.type} onChange={e=>setCatForm({...catForm, type:e.target.value})}>
                  <option value="operational">Operational</option><option value="administrative">Administrative</option>
                  <option value="vehicle">Vehicle</option><option value="salary">Salary</option><option value="other">Other</option>
                </select>
              </div>
            </div>
          </Modal>
        </div>
      )}
    </div>
  );
}
